import { useEffect, useState } from "react";
import { Button, Container, Row } from "reactstrap";

import Rule from "Pages/Tools/EmailRules/Components/Rule/Rule";
import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";
import styles from "Pages/Tools/EmailRules/EmailRules.module.css";
import AddModal from "Pages/Tools/EmailRules/Components/AddOrEditModal/AddOrEditModal";
import DeleteModal from "Pages/Tools/EmailRules/Components/DeleteModal/DeleteModal";
import TestModal from "Pages/Tools/EmailRules/Components/TestModal/TestModal";
import usePageTitle from "Hooks/usePageTitle";

export interface IEmailRule {
    name: string;
    threshhold: string;
    email_on_any_actions: boolean;
    enabled: boolean;
    integrations: string;
    users: string;
    cc_users: string;
}

export type IModal = "add" | "edit" | "delete" | "test" | "";

/** Used to map data to format react-select input wants */
export interface IOption {
    value: string;
    label: string;
}

/** Used to map data to format react-select input wants */
export interface IGroupedOption {
    label: string;
    options: IOption[];
}

export default function EmailRules() {
    const [emailRules, setEmailRules] = useState<IEmailRule[]>([]);
    const [modal, setModal] = useState<IModal>("");
    const [selectedRule, setSelectedRule] = useState<IEmailRule>();
    const [integrations, setIntegrations] = useState<IIntegration[]>([]);
    const [users, setUsers] = useState<string[]>();

    usePageTitle("Email Rules");
    const { get, put } = useFetch();
    const { newAlert } = useAlertContext();

    // Get email rules to display on page in cards
    useEffect(() => {
        let ignore = false;

        getEmailRules();

        async function getEmailRules() {
            const response = await get(`/api/email_rules`);

            let data: any;
            try {
                data = await response.json();
            } catch {
                data = response;
            }
            console.log("Get email rules response: ", data);
            if (response.ok && !ignore) {
                setEmailRules(data);
            } else {
                newAlert({
                    message: "Error loading email rules.",
                });
            }
        }
        return () => {
            ignore = true;
        };
    }, [get, newAlert]);

    // Get integrations to use in select dropdowns in add/edit modals
    useEffect(() => {
        let ignore = false;

        getIntegrations();

        async function getIntegrations() {
            const response = await get(`/api/integrations`);

            let data: Response | IIntegration[];
            try {
                data = await response.json();
            } catch {
                data = response;
            }
            console.log("Get integrations response: ", data);

            if (response.ok && !ignore) {
                setIntegrations(data as IIntegration[]);
            }
        }

        return () => {
            ignore = true;
        };
    }, [get]);

    // Get users to use in select dropdowns in add/edit modals
    useEffect(() => {
        let ignore = false;

        getUsers();

        async function getUsers() {
            const response = await get(`/api/email_rules/users`);

            let data: Response | string[];
            try {
                data = await response.json();
            } catch {
                data = response;
            }
            console.log("Get users response: ", data);

            if (response.ok && !ignore) {
                setUsers(data as string[]);
            }
        }

        return () => {
            ignore = true;
        };
    }, [get]);

    /** Handles an add/edit/delete action by overwriting all json in config_json table record
     * @param rule The rule to add or edit
     * @param action If true, deletes the rule instead of adding or editing
     */
    const editRules = async (rule: IEmailRule, action: string) => {
        let newRules: IEmailRule[] = [...emailRules];

        switch (action) {
            case "delete":
                newRules = newRules.filter((r) => r.name !== rule.name);
                break;
            case "add":
                newRules = [...emailRules, rule];
                break;
            case "edit":
                newRules = newRules.map((r) =>
                    r.name === rule.name ? rule : r
                );
                break;
        }

        const response = await put(`/api/email_rules`, newRules);

        let data: any;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Edit email rule response: ", data);

        if (response.ok) {
            setEmailRules(data);
        } else {
            newAlert({
                message: "Error editing email rule.",
            });
        }
        setModal("");
    };

    /** Handles click of test button and opens test modal */
    const handleTestClick = () => {
        setModal("test");
    };

    /** Format integration data for react-select input  */
    const integrationOptions = (integrations || []).reduce(
        (acc: IGroupedOption[], i) => {
            const groupIndex = acc.findIndex(
                (g) => g.label === i.integration_group
            );
            if (groupIndex === -1) {
                acc.push({
                    label: i.integration_group,
                    options: [{ value: i.process, label: i.name }],
                });
            } else {
                acc[groupIndex].options.push({ value: i.process, label: i.name });
            }
            return acc;
        },
        [{ label: "", options: [{ value: "%", label: "All integrations" }] }]
    );

    /** Format user data for react-select input */
    const userOptions = (users || []).map((email) => ({
        label: email,
        value: email,
    }));

    const onSetModal = (modal: IModal) => {
        setModal(modal);
    };

    const onSetSelectedRule = (rule?: IEmailRule) => {
        setSelectedRule(rule);
    };

    return (
        <Container>
            {/* Heading and add/edit/delete buttons */}
            <Row>
                <h1 className={styles.header}>Email Rules</h1>

                <span className={styles.btnBox}>
                    <Button color="primary" onClick={() => onSetModal("add")}>
                        Add Rule
                    </Button>
                    <Button color="success" onClick={handleTestClick}>
                        Test
                    </Button>
                </span>
            </Row>

            {/* Rule cards */}
            <Row className={styles.rulesBox}>
                {emailRules?.map((r) => (
                    <Rule
                        key={r.name}
                        rule={r}
                        onSetModal={onSetModal}
                        onSetSelectedRule={onSetSelectedRule}
                    />
                ))}
            </Row>

            {/* Message if no rules */}
            {emailRules?.length === 0 && <p>No email rules found.</p>}

            {/* Modals */}
            <AddModal
                editRules={editRules}
                emailRules={emailRules}
                modal={modal}
                onSetModal={onSetModal}
                integrationOptions={integrationOptions}
                userOptions={userOptions}
                selectedRule={selectedRule}
                onSetSelectedRule={onSetSelectedRule}
            />

            <TestModal
                modal={modal}
                onSetModal={onSetModal}
                integrationOptions={integrationOptions}
            />

            {selectedRule && (
                <DeleteModal
                    modal={modal}
                    onSetModal={onSetModal}
                    selectedRule={selectedRule}
                    onSetSelectedRule={onSetSelectedRule}
                    editRules={editRules}
                />
            )}
        </Container>
    );
}
